import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Stack,
  Typography,
  Alert,
  InputAdornment,
  OutlinedInput,
  Paper,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useAuthContext } from '../../context/AuthContext';
import * as userService from '../../services/userservice';
import UserProfilePage from './UserProfilePage';
import UserForm from './UserForm';
import { getRoleLabel } from '../../utils/utils';

// Importando o hook recém-criado para cuidar da lógica de status
import { useAgentStatus } from '../../services/useAgentStatus'; // <-- Atenção para o caminho correto

function UserListPage({ onUserSelect, onDataChanged, refreshKey }) {
  const [users, setUsers] = useState([]);
  const [originalUsers, setOriginalUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [openForm, setOpenForm] = useState(false);

  // Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Chamando nosso hook especializado! Uma linha para governar toda a lógica de status.
  const { agentStatus, statusError } = useAgentStatus(refreshKey);

  // Este useEffect agora tem uma única responsabilidade: buscar usuários.
  useEffect(() => {
    let isMounted = true;

    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchedUsers = await userService.getUsers();
        if (isMounted) {
          setUsers(fetchedUsers);
          setOriginalUsers(fetchedUsers);
        }
      } catch (err) {
        if (isMounted)
          setError(
            err.message || 'Ocorreu um erro desconhecido ao buscar usuários.'
          );
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchUsers();

    return () => {
      isMounted = false;
    };
  }, [refreshKey]);

  const handleSearch = (event) => {
    const { value } = event.target;
    setSearchText(value);
    const normalize = (str) =>
      str
        ?.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

    const normalizedSearch = normalize(value);

    const filtered = originalUsers.filter(
      (user) =>
        normalize(user.nome).includes(normalizedSearch) ||
        normalize(user.email).includes(normalizedSearch)
    );

    setUsers(filtered);
    setPage(0);
  };

  return (
    <Box
      sx={{
        p: 3,
        color: '#fff',
        width: '100%',
        maxWidth: '1600px',
        margin: '0 auto',
        borderRadius: 1,
      }}
    >
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {statusError && (
        <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
          Não foi possível carregar o status dos usuários em tempo real:{' '}
          {statusError}
        </Alert>
      )}
      {!loading && !error && (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
          >
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Usuários
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenForm(true)}
              sx={{
                backgroundColor: '#006A93',
                color: '#fff',
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { backgroundColor: '#005c82' },
              }}
            >
              Novo usuário
            </Button>
          </Stack>
          <Box sx={{ mb: 3 }}>
            <OutlinedInput
              fullWidth
              value={searchText}
              placeholder="Buscar usuário por nome ou email"
              onChange={handleSearch}
              startAdornment={
                <InputAdornment position="start">
                  <SvgIcon fontSize="small" sx={{ color: '#9e9e9e' }}>
                    <SearchIcon />
                  </SvgIcon>
                </InputAdornment>
              }
              sx={{
                maxWidth: 500,
                color: '#fff',
                bgcolor: '#4E4E52',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#777',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#fff',
                },
              }}
            />
          </Box>
          <Paper
            elevation={8}
            sx={{
              borderRadius: 2,
              bgcolor: '#444447',
              border: '1px solid #666',
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: '#006A93',
                      '& th': {
                        color: '#fff',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #003752',
                      },
                    }}
                  >
                    <TableCell>Nome</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user) => (
                      <TableRow
                        key={user.uid}
                        onClick={() => onUserSelect(user)}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:nth-of-type(odd)': {
                            backgroundColor: '#4E4E52',
                          },
                          '&:nth-of-type(even)': {
                            backgroundColor: '#58585B',
                          },
                          '&:hover': { backgroundColor: '#6A6A6E' },
                          '& td': { borderBottom: '1px solid #555' },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar
                              {...(user.foto ? { src: user.foto } : {})}
                              sx={{ bgcolor: '#616161', color: '#fff' }}
                            >
                              {user.nome && user.nome[0]
                                ? user.nome[0].toUpperCase()
                                : null}
                            </Avatar>
                            <Typography sx={{ fontWeight: 500, color: '#e0e0e0' }}>
                              {user.nome ? user.nome : 'Usuário'}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: '#bdbdbd' }}>
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: '#bdbdbd' }}>
                            {getRoleLabel(user.tipo)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: agentStatus[user.uid]
                                  ? '#4caf50'
                                  : '#9e9e9e',
                                boxShadow: `0 0 5px ${
                                  agentStatus[user.uid] ? '#4caf50' : 'transparent'
                                }`,
                              }}
                            />
                            <Typography
                              sx={{
                                color: agentStatus[user.uid] ? '#a5d6a7' : '#bdbdbd',
                              }}
                            >
                              {agentStatus[user.uid] ? 'Online' : 'Offline'}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={users.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                bgcolor: '#444447',
                color: '#fff',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                  {
                    color: '#ccc',
                  },
                '& .MuiTablePagination-actions': {
                  color: '#fff',
                },
                borderTop: '1px solid #666',
              }}
            />
          </Paper>
          <UserForm
            open={openForm}
            onClose={() => setOpenForm(false)}
            onUserAdded={onDataChanged}
          />
        </>
      )}
    </Box>
  );
}

export default function UsersPageController() {
  const { user } = useAuthContext();
  const [selectedUser, setSelectedUser] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
  };

  const handleBackToList = () => {
    setSelectedUser(null);
  };

  const handleDataChange = () => {
    setRefreshKey((prevKey) => prevKey + 1);
  };

  return selectedUser ? (
    <UserProfilePage
      user={selectedUser}
      currentUserId={user?.id}
      onBack={handleBackToList}
      onDataChange={handleDataChange}
      userService={userService}
    />
  ) : (
    <UserListPage
      refreshKey={refreshKey}
      onUserSelect={handleSelectUser}
      onDataChanged={handleDataChange}
    />
  );
}
